/**
 * SellerFormDialog - Dialog para criar/editar vendedores
 * 
 * Formulário modal para cadastro e edição de vendedores do CRM.
 * Campos: avatar (obrigatório), nome, email, telefone, taxa de comissão, status ativo/inativo
 * Permite vincular a usuário do sistema para herdar dados do perfil.
 * 
 * @version 1.2.0 - Adicionado Avatar3DSelector obrigatório
 */

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Link2, Link2Off } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { PhoneInput } from '@/components/ui/phone-input';
import { useCrmSellers, CrmSeller } from '@/hooks/useCrmSellers';
import { SystemUserSelector, SystemUser } from '@/components/crm/SystemUserSelector';
import { Label } from '@/components/ui/label';
import { Avatar3DSelector } from '@/components/ui/avatar-3d-selector';
import { is3DAvatarId } from '@/lib/utils/avatar-3d';
import { getStorageUrl } from '@/lib/storage';
import { supabase } from '@/integrations/supabase/client';

// Schema de validação
const sellerSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(255, 'Nome muito longo'),
  email: z.string().email('Email inválido').max(255).optional().or(z.literal('')),
  phone: z.string().max(50, 'Telefone muito longo').optional().or(z.literal('')),
  commission_rate: z.number().min(0, 'Mínimo 0%').max(100, 'Máximo 100%').default(0),
  is_active: z.boolean().default(true),
  user_id: z.string().uuid().nullable().optional(),
});

type SellerFormData = z.infer<typeof sellerSchema>;

// Props do componente
interface SellerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seller?: CrmSeller | null;
  onSuccess?: () => void;
}

/**
 * Dialog para criar/editar vendedor
 */
export function SellerFormDialog({
  open,
  onOpenChange,
  seller,
  onSuccess,
}: SellerFormDialogProps) {
  const { createSeller, updateSeller, sellers } = useCrmSellers();
  const isEditing = !!seller;

  // State para controlar herança de dados do usuário
  const [inheritFromUser, setInheritFromUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);

  // Estados para seleção de avatar (obrigatório)
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);
  const [customAvatarFile, setCustomAvatarFile] = useState<File | null>(null);
  const [customPreviewUrl, setCustomPreviewUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  // IDs de usuários já vinculados a outros vendedores
  const linkedUserIds = sellers
    .filter(s => s.user_id && s.id !== seller?.id)
    .map(s => s.user_id as string);

  // Configuração do formulário
  const form = useForm<SellerFormData>({
    resolver: zodResolver(sellerSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      commission_rate: 0,
      is_active: true,
      user_id: null,
    },
  });

  // Preenche formulário ao editar
  useEffect(() => {
    if (seller && open) {
      form.reset({
        name: seller.name,
        email: seller.email || '',
        phone: seller.phone || '',
        commission_rate: seller.commission_rate ?? 0,
        is_active: seller.is_active,
        user_id: seller.user_id || null,
      });
      
      // Carrega avatar existente do vendedor
      if (seller.avatar_url) {
        if (is3DAvatarId(seller.avatar_url)) {
          setSelectedAvatarId(seller.avatar_url);
          setCustomPreviewUrl(null);
        } else {
          // É um path de storage - gerar URL de preview
          setSelectedAvatarId(null);
          setCustomPreviewUrl(getStorageUrl(seller.avatar_url) || null);
        }
      } else {
        setSelectedAvatarId(null);
        setCustomPreviewUrl(null);
      }
      setCustomAvatarFile(null);
      setAvatarError(false);
      
      // Se tem user_id, inicializa estado do usuário
      if (seller.user_id && seller.user) {
        setSelectedUser({
          id: seller.user_id,
          full_name: seller.user.full_name,
          email: seller.user.email || null,
          avatar_url: seller.user.avatar_url,
        });
      } else {
        setSelectedUser(null);
      }
      setInheritFromUser(false);
    } else if (!seller && open) {
      form.reset({
        name: '',
        email: '',
        phone: '',
        commission_rate: 0,
        is_active: true,
        user_id: null,
      });
      setSelectedUser(null);
      setInheritFromUser(false);
      // Limpa estados de avatar
      setSelectedAvatarId(null);
      setCustomAvatarFile(null);
      setCustomPreviewUrl(null);
      setAvatarError(false);
    }
  }, [seller, open, form]);

  // Limpa erro quando avatar é selecionado
  useEffect(() => {
    if (selectedAvatarId || customAvatarFile || customPreviewUrl) {
      setAvatarError(false);
    }
  }, [selectedAvatarId, customAvatarFile, customPreviewUrl]);

  // Handler para upload de imagem customizada
  const handleCustomUpload = (file: File) => {
    setCustomAvatarFile(file);
    setCustomPreviewUrl(URL.createObjectURL(file));
    setSelectedAvatarId(null);
    setAvatarError(false);
  };

  // Handler para limpar imagem customizada
  const handleClearCustom = () => {
    setCustomAvatarFile(null);
    setCustomPreviewUrl(null);
  };

  // Handler para seleção de usuário
  const handleUserSelect = (userId: string | null, user?: SystemUser | null) => {
    form.setValue('user_id', userId);
    setSelectedUser(user || null);
    
    // Se ativou herança e selecionou usuário, preenche dados
    if (inheritFromUser && user) {
      applyUserData(user);
    }
    
    // Herda avatar do usuário se disponível
    if (user?.avatar_url) {
      if (is3DAvatarId(user.avatar_url)) {
        setSelectedAvatarId(user.avatar_url);
        setCustomPreviewUrl(null);
      } else {
        setSelectedAvatarId(null);
        setCustomPreviewUrl(getStorageUrl(user.avatar_url) || null);
      }
      setCustomAvatarFile(null);
      setAvatarError(false);
    }
  };

  // Aplica dados do usuário no formulário
  const applyUserData = (user: SystemUser) => {
    if (user.full_name) {
      form.setValue('name', user.full_name);
    }
    if (user.email) {
      form.setValue('email', user.email);
    }
  };

  // Handler para toggle de herança
  const handleInheritToggle = (enabled: boolean) => {
    setInheritFromUser(enabled);
    if (enabled && selectedUser) {
      applyUserData(selectedUser);
    }
  };

  // Handler de submissão
  const onSubmit = async (data: SellerFormData) => {
    // VALIDAÇÃO: Avatar obrigatório
    const hasAvatar = selectedAvatarId || customAvatarFile || customPreviewUrl;
    if (!hasAvatar) {
      setAvatarError(true);
      toast.error('Selecione um avatar ou envie uma foto própria');
      return;
    }

    try {
      let finalAvatarUrl: string | null = null;
      
      // Se tem arquivo customizado, faz upload para storage
      // Estrutura: sellers/{seller_uuid}/avatar.{ext} - organizado por vendedor
      if (customAvatarFile) {
        const fileExt = customAvatarFile.name.split('.').pop();
        const sellerUuid = crypto.randomUUID();
        const filePath = `sellers/${sellerUuid}/avatar.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('anexos')  // Bucket existente para anexos gerais
          .upload(filePath, customAvatarFile);
        
        if (uploadError) throw uploadError;
        finalAvatarUrl = filePath;
      } 
      // Se selecionou avatar 3D
      else if (selectedAvatarId) {
        finalAvatarUrl = selectedAvatarId;
      }
      // Se é edição e manteve preview existente (não mudou)
      // Ou se herdou avatar do usuário vinculado
      else if (customPreviewUrl) {
        finalAvatarUrl = seller?.avatar_url || 
          (selectedUser?.avatar_url && !is3DAvatarId(selectedUser.avatar_url) 
            ? selectedUser.avatar_url 
            : null);
      }

      if (isEditing && seller) {
        await updateSeller.mutateAsync({
          id: seller.id,
          name: data.name,
          email: data.email || null,
          phone: data.phone || null,
          commission_rate: data.commission_rate,
          is_active: data.is_active,
          user_id: data.user_id || null,
          avatar_url: finalAvatarUrl,
        });
      } else {
        await createSeller.mutateAsync({
          name: data.name,
          email: data.email || null,
          phone: data.phone || null,
          commission_rate: data.commission_rate,
          is_active: data.is_active,
          user_id: data.user_id || null,
          avatar_url: finalAvatarUrl,
        });
      }
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      // Exibe feedback visual de erro para o usuário
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Falha ao salvar vendedor: ${errorMessage}`);
      console.error('[SellerFormDialog] Erro ao salvar vendedor:', error);
    }
  };

  // Handler para fechar dialog
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
      setSelectedUser(null);
      setInheritFromUser(false);
      // Limpa estados de avatar
      setSelectedAvatarId(null);
      setCustomAvatarFile(null);
      setCustomPreviewUrl(null);
      setAvatarError(false);
    }
    onOpenChange(open);
  };

  const isPending = createSeller.isPending || updateSeller.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {isEditing ? 'Editar Vendedor' : 'Novo Vendedor'}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? 'Atualize as informações do vendedor.' 
              : 'Preencha os dados para cadastrar um novo vendedor.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Vínculo com Usuário do Sistema */}
            <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
              <div className="flex items-center gap-2">
                {selectedUser ? (
                  <Link2 className="h-4 w-4 text-primary" />
                ) : (
                  <Link2Off className="h-4 w-4 text-muted-foreground" />
                )}
                <Label className="text-sm font-medium">
                  Vínculo com Usuário do Sistema
                </Label>
              </div>
              
              <FormField
                control={form.control}
                name="user_id"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <SystemUserSelector
                        value={field.value}
                        onSelect={handleUserSelect}
                        disabled={isPending}
                        excludeUserIds={linkedUserIds}
                        placeholder="Selecionar usuário (opcional)..."
                        // Fallback: garante exibição do usuário vinculado já existente
                        // mesmo se ele ainda não está no cache do useTenantMembers,
                        // evitando a falsa impressão de "sem vínculo" no modo edição.
                        fallbackUser={
                          seller?.user_id && seller.user
                            ? {
                                id: seller.user_id,
                                full_name: seller.user.full_name,
                                email: seller.user.email,
                                avatar_url: seller.user.avatar_url,
                              }
                            : null
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Vincule a um usuário interno para herdar avatar e dados
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Switch para herdar dados */}
              {selectedUser && (
                <div className="flex items-center justify-between pt-2">
                  <div className="space-y-0.5">
                    <Label className="text-sm">Herdar dados do perfil</Label>
                    <p className="text-xs text-muted-foreground">
                      Preenche nome e email com dados do usuário
                    </p>
                  </div>
                  <Switch
                    checked={inheritFromUser}
                    onCheckedChange={handleInheritToggle}
                    disabled={isPending}
                  />
                </div>
              )}
            </div>

            {/* Seleção de Avatar - Obrigatório */}
            <Avatar3DSelector
              value={selectedAvatarId}
              onChange={(id) => {
                setSelectedAvatarId(id);
                setCustomAvatarFile(null);
                setCustomPreviewUrl(null);
              }}
              onCustomUpload={handleCustomUpload}
              customPreviewUrl={customPreviewUrl}
              onClearCustom={handleClearCustom}
              disabled={isPending}
              label="Avatar"
              required={true}
              error={avatarError}
            />

            {/* Nome */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome do vendedor" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="email@exemplo.com" 
                      type="email" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Telefone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="11 99999-9999"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormDescription>
                    Telefone de contato do vendedor
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Taxa de Comissão */}
            <FormField
              control={form.control}
              name="commission_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Taxa de Comissão (%)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      placeholder="0"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
                    />
                  </FormControl>
                  <FormDescription>
                    Percentual de comissão sobre vendas (0 a 100%)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Status Ativo */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Ativo</FormLabel>
                    <FormDescription>
                      Vendedores inativos não aparecem nas seleções
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            </div>

            <DialogFooter className="pt-4 border-t shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditing ? 'Salvar Alterações' : 'Criar Vendedor'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
